/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import type {ContactDetailsFilter, FilterOperation} from '@agent-assist/api-typescript-react-query-hooks';
import {GridAggregationFunction, type GridFilterModel} from '@mui/x-data-grid-premium';

export function makeColumnNamePrettier(key: string) {
  return key
    .replace(/pd$/, '')
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

export function looksLikeDateField(key: string) {
  const lower = key.toLowerCase();
  return lower.includes('date') || lower.endsWith('createdat') || lower.endsWith('updatedat');
}

export function looksLikeNumberField(key: string) {
  const lower = key.toLowerCase();
  return lower.startsWith('total') || lower.endsWith('score');
}

export function mapOperatorToFilterOperation(operator: string): FilterOperation {
  switch (operator) {
    case 'equals':
      return 'EQUALS_OPERATOR';
    case 'contains':
      return 'CONTAINS_OPERATOR';
    case '>':
      return 'GT_OPERATOR';
    case '>=':
      return 'GTE_OPERATOR';
    case '<':
      return 'LT_OPERATOR';
    case '<=':
      return 'LTE_OPERATOR';
    case 'startsWith':
      return 'STARTS_WITH_STRING_OPERATOR';
    case 'endsWith':
      return 'ENDS_WITH_STRING_OPERATOR';
    default:
      return 'EQUALS_OPERATOR';
  }
}

export function convertFilters(input: GridFilterModel): Array<ContactDetailsFilter> {
  const filters: ContactDetailsFilter[] = [];
  for (const item of input.items) {
    const value = item.value?.toString();
    if (value !== undefined) {
      filters.push({
        key: item.field,
        value: item.field === 'status' ? (value === 'INCOMPLETE' ? 'FAILED' : value) : value,
        operation: mapOperatorToFilterOperation(item.operator),
      });
    }
  }
  return filters;
}

// Helper function to extract a numeric value from a cell value which may be an object
function extractNumber(value: any): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'object' && value.number !== undefined) {
    const num = Number(value.number);
    return isNaN(num) ? null : num;
  }
  const num = Number(value);
  return isNaN(num) ? null : num;
}

const visualiseAggregation: GridAggregationFunction = {
  apply: ({values}) => {
    const data = values
      .filter((item) => item && item.time)
      .map((item) => ({
        time: item.time,
        value: item.number,
        id: item.id,
      }));
    return data;
  },
  columnTypes: ['number', 'percentage'],
  label: 'visualise',
};

const sumAggregation: GridAggregationFunction = {
  apply: ({values}) => {
    let sum = 0;
    let found = false;
    values.forEach((value) => {
      const num = extractNumber(value);
      if (num !== null) {
        sum += num;
        found = true;
      }
    });
    return found ? {number: sum} : null;
  },
  columnTypes: ['number', 'percentage'],
  label: 'sum',
};

const avgAggregation: GridAggregationFunction = {
  apply: ({values}) => {
    let sum = 0;
    let count = 0;
    values.forEach((value) => {
      const num = extractNumber(value);
      if (num !== null) {
        sum += num;
        count++;
      }
    });
    return count > 0 ? {number: sum / count} : null;
  },
  columnTypes: ['number', 'percentage'],
  label: 'avg',
};

const minAggregation: GridAggregationFunction = {
  apply: ({values}) => {
    let min: number | null = null;
    values.forEach((value) => {
      const num = extractNumber(value);
      if (num !== null) {
        if (min === null || num < min) {
          min = num;
        }
      }
    });
    return min !== null ? {number: min} : null;
  },
  columnTypes: ['number', 'percentage'],
  label: 'min',
};

const maxAggregation: GridAggregationFunction = {
  apply: ({values}) => {
    let max: number | null = null;
    values.forEach((value) => {
      const num = extractNumber(value);
      if (num !== null) {
        if (max === null || num > max) {
          max = num;
        }
      }
    });
    return max !== null ? {number: max} : null;
  },
  columnTypes: ['number', 'percentage'],
  label: 'max',
};

const sizeAggregation: GridAggregationFunction = {
  apply: ({values}) => {
    const count = values.filter((value) => value !== null && value !== undefined).length;
    return {number: count};
  },
  columnTypes: ['number', 'percentage'],
  label: 'size',
};

export const aggregationFunctions = {
  visualise: visualiseAggregation,
  sum: sumAggregation,
  avg: avgAggregation,
  min: minAggregation,
  max: maxAggregation,
  size: sizeAggregation,
};
